import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import toast from 'react-hot-toast';
import { 
  Plus, 
  Save, 
  Trash2, 
  Search, 
  Database,
  Building,
  MapPin,
  Users,
  GraduationCap,
  Edit3,
  AlertCircle,
  Award,
  BookOpen,
  Filter,
  ChevronDown,
  ChevronUp,
  Calendar,
  Target
} from 'lucide-react';
import { josaaService, type JoSAAInstitute, type JoSAABranch, type JoSAACutoff } from '../lib/josaaService';
import { useAuth } from '../contexts/AuthContext';
import { Link } from 'react-router-dom';

const JoSAAAdmin = () => {
  const { isAdmin } = useAuth();
  const [institutes, setInstitutes] = useState<JoSAAInstitute[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [stateFilter, setStateFilter] = useState('all');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [saving, setSaving] = useState(false);

  // Branch management
  const [expandedInstitutes, setExpandedInstitutes] = useState<string[]>([]);
  const [showAddBranchForm, setShowAddBranchForm] = useState<string | null>(null);
  const [editingBranchId, setEditingBranchId] = useState<string | null>(null);

  // Cutoff management
  const [expandedBranches, setExpandedBranches] = useState<string[]>([]);
  const [showAddCutoffForm, setShowAddCutoffForm] = useState<string | null>(null);
  const [editingCutoffId, setEditingCutoffId] = useState<string | null>(null);

  const [newInstitute, setNewInstitute] = useState({
    name: '',
    short_name: '',
    institute_type: 'IIT' as 'IIT' | 'NIT' | 'IIIT' | 'GFTI',
    location: '',
    state: '',
    established: '',
    website: '',
    nirf_ranking: 0,
    image_url: 'https://images.pexels.com/photos/1454360/pexels-photo-1454360.jpeg?auto=compress&cs=tinysrgb&w=800',
    is_active: true
  });

  const [newBranch, setNewBranch] = useState({
    branch_name: '',
    branch_code: '',
    degree_type: 'B.Tech',
    duration: '4 years',
    seats_total: 0,
    is_active: true
  });

  const [newCutoff, setNewCutoff] = useState({
    year: new Date().getFullYear().toString(),
    round_number: 1,
    round_name: 'Round 1',
    quota: 'All India' as 'All India' | 'Home State' | 'Other State',
    seat_type: 'Gender-Neutral' as 'Gender-Neutral' | 'Female-only (including Supernumerary)' | 'Supernumerary',
    category: 'General' as 'General' | 'OBC-NCL' | 'EWS' | 'SC' | 'ST',
    opening_rank: 0,
    closing_rank: 0,
    seats_available: 0,
    seats_filled: 0
  });

  const instituteTypes = ['all', 'IIT', 'NIT', 'IIIT', 'GFTI'];
  const states = [
    'all', 'Andhra Pradesh', 'Assam', 'Bihar', 'Chhattisgarh', 'Delhi', 'Goa', 'Gujarat',
    'Haryana', 'Himachal Pradesh', 'Jharkhand', 'Karnataka', 'Kerala', 'Madhya Pradesh',
    'Maharashtra', 'Manipur', 'Meghalaya', 'Mizoram', 'Nagaland', 'Odisha', 'Punjab',
    'Rajasthan', 'Sikkim', 'Tamil Nadu', 'Telangana', 'Tripura', 'Uttar Pradesh',
    'Uttarakhand', 'West Bengal', 'Chandigarh', 'Puducherry'
  ];

  const quotaTypes = ['All India', 'Home State', 'Other State'];
  const seatTypes = ['Gender-Neutral', 'Female-only (including Supernumerary)', 'Supernumerary'];
  const categories = ['General', 'OBC-NCL', 'EWS', 'SC', 'ST'];

  useEffect(() => {
    if (isAdmin) {
      fetchInstitutes();
    }
  }, [isAdmin, searchQuery, typeFilter, stateFilter]);

  const fetchInstitutes = async () => {
    try {
      setLoading(true);
      const filters: any = {};
      
      if (typeFilter !== 'all') {
        filters.type = typeFilter;
      }
      
      if (stateFilter !== 'all') {
        filters.state = stateFilter;
      }
      
      if (searchQuery) {
        filters.search = searchQuery;
      }
      
      const data = await josaaService.getInstitutes(filters);
      setInstitutes(data);
    } catch (error) {
      console.error('Error fetching institutes:', error);
      toast.error('Failed to fetch institutes');
    } finally {
      setLoading(false);
    }
  };

  const toggleInstituteExpand = (instituteId: string) => {
    setExpandedInstitutes(prev => 
      prev.includes(instituteId) 
        ? prev.filter(id => id !== instituteId) 
        : [...prev, instituteId]
    );
  };

  const toggleBranchExpand = (branchId: string) => {
    setExpandedBranches(prev => 
      prev.includes(branchId) 
        ? prev.filter(id => id !== branchId) 
        : [...prev, branchId]
    );
  };

  const validateInstituteForm = () => {
    if (!newInstitute.name.trim()) {
      toast.error('Institute name is required');
      return false;
    }
    if (!newInstitute.short_name.trim()) {
      toast.error('Short name is required');
      return false;
    }
    if (!newInstitute.location.trim()) {
      toast.error('Location is required');
      return false;
    }
    if (!newInstitute.state) {
      toast.error('State is required');
      return false;
    }
    return true;
  };

  const validateBranchForm = () => {
    if (!newBranch.branch_name.trim()) {
      toast.error('Branch name is required');
      return false;
    }
    if (!newBranch.branch_code.trim()) {
      toast.error('Branch code is required');
      return false;
    }
    if (newBranch.seats_total <= 0) {
      toast.error('Total seats must be greater than 0');
      return false;
    }
    return true;
  };

  const validateCutoffForm = () => {
    if (!newCutoff.year.trim()) {
      toast.error('Year is required');
      return false;
    }
    if (newCutoff.round_number <= 0) {
      toast.error('Round number must be greater than 0');
      return false;
    }
    if (newCutoff.closing_rank <= 0) {
      toast.error('Closing rank must be greater than 0');
      return false;
    }
    return true;
  };

  const saveInstitute = async () => {
    if (!validateInstituteForm()) return;

    setSaving(true);
    try {
      if (editingId) {
        await josaaService.updateInstitute(editingId, newInstitute);
        toast.success('Institute updated successfully!');
        setEditingId(null);
      } else {
        await josaaService.createInstitute(newInstitute);
        toast.success('Institute added successfully!');
        setShowAddForm(false);
      }
      
      resetInstituteForm();
      fetchInstitutes();
    } catch (error) {
      console.error('Error saving institute:', error);
      toast.error('Failed to save institute');
    } finally {
      setSaving(false);
    }
  };

  const saveBranch = async () => {
    if (!validateBranchForm() || !showAddBranchForm) return;

    setSaving(true);
    try {
      if (editingBranchId) {
        await josaaService.updateBranch(editingBranchId, newBranch);
        toast.success('Branch updated successfully!');
        setEditingBranchId(null);
      } else {
        await josaaService.createBranch({
          ...newBranch,
          institute_id: showAddBranchForm
        });
        toast.success('Branch added successfully!');
      }
      
      setShowAddBranchForm(null);
      resetBranchForm();
      fetchInstitutes();
    } catch (error) {
      console.error('Error saving branch:', error);
      toast.error('Failed to save branch');
    } finally {
      setSaving(false);
    }
  };

  const saveCutoff = async () => {
    if (!validateCutoffForm() || !showAddCutoffForm) return;

    setSaving(true);
    try {
      if (editingCutoffId) {
        await josaaService.updateCutoff(editingCutoffId, newCutoff);
        toast.success('Cutoff updated successfully!');
        setEditingCutoffId(null);
      } else {
        await josaaService.createCutoff({
          ...newCutoff,
          branch_id: showAddCutoffForm
        });
        toast.success('Cutoff added successfully!');
      }
      
      setShowAddCutoffForm(null);
      resetCutoffForm();
      fetchInstitutes();
    } catch (error) {
      console.error('Error saving cutoff:', error);
      toast.error('Failed to save cutoff');
    } finally {
      setSaving(false);
    }
  };

  const editInstitute = (institute: JoSAAInstitute) => {
    setNewInstitute({
      name: institute.name,
      short_name: institute.short_name,
      institute_type: institute.institute_type,
      location: institute.location,
      state: institute.state,
      established: institute.established || '',
      website: institute.website || '',
      nirf_ranking: institute.nirf_ranking || 0,
      image_url: institute.image_url,
      is_active: institute.is_active
    });
    setEditingId(institute.id);
    setShowAddForm(true);
  };

  const editBranch = (branch: JoSAABranch) => {
    setNewBranch({
      branch_name: branch.branch_name,
      branch_code: branch.branch_code,
      degree_type: branch.degree_type,
      duration: branch.duration,
      seats_total: branch.seats_total || 0,
      is_active: branch.is_active
    });
    setEditingBranchId(branch.id);
    setShowAddBranchForm(branch.institute_id);
  };

  const editCutoff = (cutoff: JoSAACutoff) => {
    setNewCutoff({
      year: cutoff.year,
      round_number: cutoff.round_number,
      round_name: cutoff.round_name,
      quota: cutoff.quota,
      seat_type: cutoff.seat_type,
      category: cutoff.category,
      opening_rank: cutoff.opening_rank || 0,
      closing_rank: cutoff.closing_rank,
      seats_available: cutoff.seats_available,
      seats_filled: cutoff.seats_filled
    });
    setEditingCutoffId(cutoff.id);
    setShowAddCutoffForm(cutoff.branch_id);
  };

  const deleteInstitute = async (institute: JoSAAInstitute) => {
    if (!confirm(`Are you sure you want to delete ${institute.name}?`)) return;

    try {
      await josaaService.deleteInstitute(institute.id);
      setInstitutes(prev => prev.filter(i => i.id !== institute.id));
      toast.success('Institute deleted successfully!');
    } catch (error) {
      console.error('Error deleting institute:', error);
      toast.error('Failed to delete institute');
    }
  };

  const deleteBranch = async (branchId: string) => {
    if (!confirm('Are you sure you want to delete this branch? This will also delete all cutoffs for this branch.')) return;

    try {
      await josaaService.deleteBranch(branchId);
      toast.success('Branch deleted successfully!');
      fetchInstitutes();
    } catch (error) {
      console.error('Error deleting branch:', error);
      toast.error('Failed to delete branch');
    }
  };

  const deleteCutoff = async (cutoffId: string) => {
    if (!confirm('Are you sure you want to delete this cutoff?')) return;

    try {
      await josaaService.deleteCutoff(cutoffId);
      toast.success('Cutoff deleted successfully!');
      fetchInstitutes();
    } catch (error) {
      console.error('Error deleting cutoff:', error);
      toast.error('Failed to delete cutoff');
    }
  };

  const resetInstituteForm = () => {
    setNewInstitute({
      name: '',
      short_name: '',
      institute_type: 'IIT',
      location: '',
      state: '',
      established: '',
      website: '',
      nirf_ranking: 0,
      image_url: 'https://images.pexels.com/photos/1454360/pexels-photo-1454360.jpeg?auto=compress&cs=tinysrgb&w=800',
      is_active: true
    });
  };

  const resetBranchForm = () => {
    setNewBranch({
      branch_name: '',
      branch_code: '',
      degree_type: 'B.Tech',
      duration: '4 years',
      seats_total: 0,
      is_active: true
    });
  };

  const resetCutoffForm = () => {
    setNewCutoff({
      year: new Date().getFullYear().toString(),
      round_number: 1,
      round_name: 'Round 1',
      quota: 'All India',
      seat_type: 'Gender-Neutral',
      category: 'General',
      opening_rank: 0,
      closing_rank: 0,
      seats_available: 0,
      seats_filled: 0
    });
  };

  const handleInstituteInputChange = (field: string, value: any) => {
    setNewInstitute(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleBranchInputChange = (field: string, value: any) => {
    setNewBranch(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleCutoffInputChange = (field: string, value: any) => {
    setNewCutoff(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Redirect if not admin
  if (!isAdmin) {
    return (
      <div style={{ paddingTop: '80px' }} className="bg-light min-vh-100">
        <div className="container py-5">
          <div className="text-center">
            <div className="card border-0 shadow-lg mx-auto" style={{ maxWidth: '600px' }}>
              <div className="card-body p-5">
                <div className="mb-4">
                  <AlertCircle className="text-warning mx-auto mb-3" size={64} />
                </div>
                <h1 className="h2 text-dark mb-3">Access Restricted</h1>
                <p className="text-muted mb-4">
                  You need admin privileges to access the JoSAA Management system.
                </p>
                <Link to="/" className="btn btn-primary">
                  Back to Home
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div style={{ paddingTop: '80px' }} className="bg-light min-vh-100">
        <div className="container py-5">
          <div className="text-center">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <p className="mt-3 text-muted">Loading JoSAA institutes...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ paddingTop: '80px' }} className="bg-light min-vh-100">
      <div className="container py-4">
        {/* Header */}
        <div className="d-flex align-items-center justify-content-between mb-4">
          <div className="d-flex align-items-center gap-3">
            <div className="bg-primary rounded-3 p-3">
              <Database className="text-white" size={32} />
            </div>
            <div>
              <h1 className="h2 fw-bold text-dark mb-1">JoSAA Institute Manager</h1>
              <p className="text-muted mb-0">Manage IIT, NIT, IIIT, and GFTI institutes with branches and cutoffs</p>
            </div>
          </div>
          <Link to="/admin" className="btn btn-outline-secondary">
            Back to Admin
          </Link>
        </div>

        {/* Filters */}
        <div className="card border-0 shadow-lg mb-4">
          <div className="card-body p-4">
            <div className="row g-3 align-items-center">
              <div className="col-md-3">
                <button
                  onClick={() => {
                    resetInstituteForm();
                    setShowAddForm(true);
                    setEditingId(null);
                  }}
                  className="btn btn-success w-100 d-flex align-items-center justify-content-center gap-2"
                >
                  <Plus size={16} />
                  Add Institute
                </button>
              </div>
              
              <div className="col-md-2">
                <select
                  value={typeFilter}
                  onChange={(e) => setTypeFilter(e.target.value)}
                  className="form-select"
                >
                  {instituteTypes.map(type => (
                    <option key={type} value={type}>
                      {type === 'all' ? 'All Types' : type}
                    </option>
                  ))}
                </select>
              </div>
              
              <div className="col-md-3">
                <select
                  value={stateFilter}
                  onChange={(e) => setStateFilter(e.target.value)}
                  className="form-select"
                >
                  {states.map(state => (
                    <option key={state} value={state}>
                      {state === 'all' ? 'All States' : state}
                    </option>
                  ))}
                </select>
              </div>
              
              <div className="col-md-4">
                <div className="position-relative">
                  <Search className="position-absolute top-50 start-0 translate-middle-y ms-3 text-muted" size={16} />
                  <input
                    type="text"
                    placeholder="Search institutes..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="form-control ps-5"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Stats */}
        <div className="row g-3 mb-4">
          <div className="col-md-3">
            <div className="card bg-primary bg-opacity-10 border-0">
              <div className="card-body text-center p-3">
                <h5 className="fw-bold text-primary mb-1">{institutes.length}</h5>
                <small className="text-muted">Total Institutes</small>
              </div>
            </div>
          </div>
          <div className="col-md-3">
            <div className="card bg-success bg-opacity-10 border-0">
              <div className="card-body text-center p-3">
                <h5 className="fw-bold text-success mb-1">
                  {institutes.filter(i => i.institute_type === 'IIT').length}
                </h5>
                <small className="text-muted">IITs</small>
              </div>
            </div>
          </div>
          <div className="col-md-3">
            <div className="card bg-warning bg-opacity-10 border-0">
              <div className="card-body text-center p-3">
                <h5 className="fw-bold text-warning mb-1">
                  {institutes.filter(i => i.institute_type === 'NIT').length}
                </h5>
                <small className="text-muted">NITs</small>
              </div>
            </div>
          </div>
          <div className="col-md-3">
            <div className="card bg-info bg-opacity-10 border-0">
              <div className="card-body text-center p-3">
                <h5 className="fw-bold text-info mb-1">
                  {institutes.filter(i => i.institute_type === 'IIIT').length}
                </h5>
                <small className="text-muted">IIITs</small>
              </div>
            </div>
          </div>
        </div>

        {/* Add/Edit Institute Form */}
        {showAddForm && (
          <motion.div 
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="card border-0 shadow-lg mb-4"
          >
            <div className="card-header bg-primary text-white">
              <h5 className="mb-0">{editingId ? 'Edit Institute' : 'Add New Institute'}</h5>
            </div>
            <div className="card-body p-4">
              <div className="row g-3">
                <div className="col-md-6">
                  <label className="form-label fw-semibold">Institute Name *</label>
                  <input
                    type="text"
                    value={newInstitute.name}
                    onChange={(e) => handleInstituteInputChange('name', e.target.value)}
                    className="form-control"
                    placeholder="e.g., Indian Institute of Technology Delhi"
                  />
                </div>
                <div className="col-md-6">
                  <label className="form-label fw-semibold">Short Name *</label>
                  <input
                    type="text"
                    value={newInstitute.short_name}
                    onChange={(e) => handleInstituteInputChange('short_name', e.target.value)}
                    className="form-control"
                    placeholder="e.g., IIT Delhi"
                  />
                </div>
                <div className="col-md-4">
                  <label className="form-label fw-semibold">Institute Type *</label>
                  <select
                    value={newInstitute.institute_type}
                    onChange={(e) => handleInstituteInputChange('institute_type', e.target.value)}
                    className="form-select"
                  >
                    <option value="IIT">IIT</option>
                    <option value="NIT">NIT</option>
                    <option value="IIIT">IIIT</option>
                    <option value="GFTI">GFTI</option>
                  </select>
                </div>
                <div className="col-md-4">
                  <label className="form-label fw-semibold">Location *</label>
                  <input
                    type="text"
                    value={newInstitute.location}
                    onChange={(e) => handleInstituteInputChange('location', e.target.value)}
                    className="form-control"
                    placeholder="e.g., New Delhi"
                  />
                </div>
                <div className="col-md-4">
                  <label className="form-label fw-semibold">State *</label>
                  <select
                    value={newInstitute.state}
                    onChange={(e) => handleInstituteInputChange('state', e.target.value)}
                    className="form-select"
                  >
                    <option value="">Select State</option>
                    {states.slice(1).map(state => (
                      <option key={state} value={state}>{state}</option>
                    ))}
                  </select>
                </div>
                <div className="col-md-4">
                  <label className="form-label fw-semibold">Established</label>
                  <input
                    type="text"
                    value={newInstitute.established}
                    onChange={(e) => handleInstituteInputChange('established', e.target.value)}
                    className="form-control"
                    placeholder="e.g., 1961"
                  />
                </div>
                <div className="col-md-4">
                  <label className="form-label fw-semibold">NIRF Ranking</label>
                  <input
                    type="number"
                    value={newInstitute.nirf_ranking}
                    onChange={(e) => handleInstituteInputChange('nirf_ranking', parseInt(e.target.value) || 0)}
                    className="form-control"
                    placeholder="Enter NIRF ranking"
                    min="0"
                  />
                </div>
                <div className="col-md-4">
                  <div className="form-check mt-4">
                    <input
                      type="checkbox"
                      id="is_active"
                      checked={newInstitute.is_active}
                      onChange={(e) => handleInstituteInputChange('is_active', e.target.checked)}
                      placeholder="https://institute.edu (Required for 'View Details' button)"
                    />
                    <div className="form-text">
                      This URL will be used for the "View Institution Details" button in predictions
                    </div>
                    <label className="form-check-label fw-semibold" htmlFor="is_active">
                      Active Institute
                    </label>
                  </div>
                </div>
                <div className="col-md-6">
                  <label className="form-label fw-semibold">Website</label>
                  <input
                    type="url"
                    value={newInstitute.website}
                    onChange={(e) => handleInstituteInputChange('website', e.target.value)}
                    className="form-control"
                    placeholder="https://www.iitd.ac.in"
                  />
                </div>
                <div className="col-md-6">
                  <label className="form-label fw-semibold">Image URL</label>
                  <input
                    type="url"
                    value={newInstitute.image_url}
                    onChange={(e) => handleInstituteInputChange('image_url', e.target.value)}
                    className="form-control"
                    placeholder="https://images.pexels.com/..."
                  />
                </div>
              </div>
              
              <div className="d-flex gap-2 mt-4">
                <button
                  onClick={saveInstitute}
                  disabled={saving}
                  className="btn btn-primary d-flex align-items-center gap-2"
                >
                  {saving ? (
                    <>
                      <span className="spinner-border spinner-border-sm" role="status"></span>
                      Saving...
                    </>
                  ) : (
                    <>
                      <Save size={16} />
                      {editingId ? 'Update Institute' : 'Save Institute'}
                    </>
                  )}
                </button>
                <button
                  onClick={() => {
                    setEditingId(null);
                    setShowAddForm(false);
                    resetInstituteForm();
                  }}
                  className="btn btn-secondary"
                >
                  Cancel
                </button>
              </div>
            </div>
          </motion.div>
        )}

        {/* Institutes List with Branches and Cutoffs */}
        <div className="vstack gap-4">
          {institutes.map((institute) => (
            <div key={institute.id} className="card border-0 shadow-lg">
              <div 
                className="card-header bg-light d-flex justify-content-between align-items-center"
                style={{ cursor: 'pointer' }}
                onClick={() => toggleInstituteExpand(institute.id)}
              >
                <div className="d-flex align-items-center gap-3">
                  <img
                    src={institute.image_url}
                    alt={institute.name}
                    className="rounded"
                    style={{ height: '50px', width: '50px', objectFit: 'cover' }}
                  />
                  <div>
                    <h5 className="fw-bold text-dark mb-1">{institute.name}</h5>
                    <div className="d-flex align-items-center gap-3 text-muted small">
                      <span>{institute.institute_type}</span>
                      <span>•</span>
                      <span>{institute.location}, {institute.state}</span>
                      <span>•</span>
                      <span>NIRF: #{institute.nirf_ranking || 'N/A'}</span>
                    </div>
                  </div>
                </div>
                <div className="d-flex gap-3 align-items-center">
                  <div className="d-flex gap-2">
                    <span className={`badge ${institute.is_active ? 'bg-success' : 'bg-secondary'}`}>
                      {institute.is_active ? 'Active' : 'Inactive'}
                    </span>
                    <span className="badge bg-primary">{institute.branches?.length || 0} Branches</span>
                  </div>
                  <div className="d-flex gap-2">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        editInstitute(institute);
                      }}
                      className="btn btn-sm btn-outline-primary"
                    >
                      <Edit3 size={14} />
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        deleteInstitute(institute);
                      }}
                      className="btn btn-sm btn-outline-danger"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                  {expandedInstitutes.includes(institute.id) ? (
                    <ChevronUp size={16} />
                  ) : (
                    <ChevronDown size={16} />
                  )}
                </div>
              </div>

              {expandedInstitutes.includes(institute.id) && (
                <div className="card-body p-4">
                  {/* Add Branch Button */}
                  <div className="d-flex justify-content-between align-items-center mb-4">
                    <h6 className="fw-semibold text-dark mb-0">Branches & Cutoffs</h6>
                    <button
                      onClick={() => {
                        resetBranchForm();
                        setShowAddBranchForm(institute.id);
                        setEditingBranchId(null);
                      }}
                      className="btn btn-sm btn-success d-flex align-items-center gap-2"
                    >
                      <Plus size={14} />
                      Add Branch
                    </button>
                  </div>

                  {/* Add/Edit Branch Form */}
                  {showAddBranchForm === institute.id && (
                    <motion.div 
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="card border mb-4"
                    >
                      <div className="card-header bg-light">
                        <h6 className="mb-0">{editingBranchId ? 'Edit Branch' : 'Add New Branch'}</h6>
                      </div>
                      <div className="card-body">
                        <div className="row g-3">
                          <div className="col-md-6">
                            <label className="form-label fw-semibold">Branch Name *</label>
                            <input
                              type="text"
                              value={newBranch.branch_name}
                              onChange={(e) => handleBranchInputChange('branch_name', e.target.value)}
                              className="form-control"
                              placeholder="e.g., Computer Science and Engineering"
                            />
                          </div>
                          <div className="col-md-6">
                            <label className="form-label fw-semibold">Branch Code *</label>
                            <input
                              type="text"
                              value={newBranch.branch_code}
                              onChange={(e) => handleBranchInputChange('branch_code', e.target.value)}
                              className="form-control"
                              placeholder="e.g., CS"
                            />
                          </div>
                          <div className="col-md-4">
                            <label className="form-label fw-semibold">Degree Type</label>
                            <input
                              type="text"
                              value={newBranch.degree_type}
                              onChange={(e) => handleBranchInputChange('degree_type', e.target.value)}
                              className="form-control"
                              placeholder="e.g., B.Tech"
                            />
                          </div>
                          <div className="col-md-4">
                            <label className="form-label fw-semibold">Duration</label>
                            <input
                              type="text"
                              value={newBranch.duration}
                              onChange={(e) => handleBranchInputChange('duration', e.target.value)}
                              className="form-control"
                              placeholder="e.g., 4 years"
                            />
                          </div>
                          <div className="col-md-4">
                            <label className="form-label fw-semibold">Total Seats *</label>
                            <input
                              type="number"
                              value={newBranch.seats_total}
                              onChange={(e) => handleBranchInputChange('seats_total', parseInt(e.target.value))}
                              className="form-control"
                              placeholder="Enter total seats"
                              min="0"
                            />
                          </div>
                          <div className="col-md-4">
                            <div className="form-check mt-4">
                              <input
                                type="checkbox"
                                id="branch_active"
                                checked={newBranch.is_active}
                                onChange={(e) => handleBranchInputChange('is_active', e.target.checked)}
                                className="form-check-input"
                              />
                              <label className="form-check-label fw-semibold" htmlFor="branch_active">
                                Active Branch
                              </label>
                            </div>
                          </div>
                        </div>
                        
                        <div className="d-flex gap-2 mt-4">
                          <button
                            onClick={saveBranch}
                            disabled={saving}
                            className="btn btn-primary d-flex align-items-center gap-2"
                          >
                            {saving ? (
                              <>
                                <span className="spinner-border spinner-border-sm" role="status"></span>
                                Saving...
                              </>
                            ) : (
                              <>
                                <Save size={16} />
                                {editingBranchId ? 'Update Branch' : 'Save Branch'}
                              </>
                            )}
                          </button>
                          <button
                            onClick={() => {
                              setShowAddBranchForm(null);
                              setEditingBranchId(null);
                              resetBranchForm();
                            }}
                            className="btn btn-secondary"
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    </motion.div>
                  )}

                  {/* Branches List */}
                  {institute.branches && institute.branches.length > 0 ? (
                    <div className="vstack gap-3">
                      {institute.branches.map((branch) => (
                        <div key={branch.id} className="card border">
                          <div 
                            className="card-header bg-light d-flex justify-content-between align-items-center"
                            style={{ cursor: 'pointer' }}
                            onClick={() => toggleBranchExpand(branch.id)}
                          >
                            <div className="d-flex align-items-center gap-2">
                              <h6 className="mb-0 fw-bold">{branch.branch_name} ({branch.branch_code})</h6>
                              <span className="badge bg-primary">{branch.degree_type}</span>
                              <span className="badge bg-secondary">{branch.duration}</span>
                              <span className="badge bg-success">{branch.seats_total} seats</span>
                            </div>
                            <div className="d-flex gap-3 align-items-center">
                              <div className="d-flex gap-2">
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    editBranch(branch);
                                  }}
                                  className="btn btn-sm btn-outline-primary"
                                >
                                  <Edit3 size={14} />
                                </button>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    deleteBranch(branch.id);
                                  }}
                                  className="btn btn-sm btn-outline-danger"
                                >
                                  <Trash2 size={14} />
                                </button>
                              </div>
                              {expandedBranches.includes(branch.id) ? (
                                <ChevronUp size={16} />
                              ) : (
                                <ChevronDown size={16} />
                              )}
                            </div>
                          </div>

                          {expandedBranches.includes(branch.id) && (
                            <div className="card-body">
                              {/* Add Cutoff Button */}
                              <div className="d-flex justify-content-between align-items-center mb-3">
                                <h6 className="fw-semibold text-dark mb-0">Cutoffs</h6>
                                <button
                                  onClick={() => {
                                    resetCutoffForm();
                                    setShowAddCutoffForm(branch.id);
                                    setEditingCutoffId(null);
                                  }}
                                  className="btn btn-sm btn-info d-flex align-items-center gap-2"
                                >
                                  <Plus size={14} />
                                  Add Cutoff
                                </button>
                              </div>

                              {/* Add/Edit Cutoff Form */}
                              {showAddCutoffForm === branch.id && (
                                <motion.div 
                                  initial={{ opacity: 0, y: -10 }}
                                  animate={{ opacity: 1, y: 0 }}
                                  className="card border mb-3"
                                >
                                  <div className="card-header bg-light">
                                    <h6 className="mb-0">{editingCutoffId ? 'Edit Cutoff' : 'Add New Cutoff'}</h6>
                                  </div>
                                  <div className="card-body">
                                    <div className="row g-3">
                                      <div className="col-md-3">
                                        <label className="form-label fw-semibold">Year *</label>
                                        <input
                                          type="text"
                                          value={newCutoff.year}
                                          onChange={(e) => handleCutoffInputChange('year', e.target.value)}
                                          className="form-control"
                                          placeholder="e.g., 2024"
                                        />
                                      </div>
                                      <div className="col-md-3">
                                        <label className="form-label fw-semibold">Round Number *</label>
                                        <input
                                          type="number"
                                          value={newCutoff.round_number}
                                          onChange={(e) => handleCutoffInputChange('round_number', parseInt(e.target.value))}
                                          className="form-control"
                                          placeholder="e.g., 1"
                                          min="1"
                                        />
                                      </div>
                                      <div className="col-md-3">
                                        <label className="form-label fw-semibold">Round Name</label>
                                        <input
                                          type="text"
                                          value={newCutoff.round_name}
                                          onChange={(e) => handleCutoffInputChange('round_name', e.target.value)}
                                          className="form-control"
                                          placeholder="e.g., Round 1"
                                        />
                                      </div>
                                      <div className="col-md-3">
                                        <label className="form-label fw-semibold">Quota *</label>
                                        <select
                                          value={newCutoff.quota}
                                          onChange={(e) => handleCutoffInputChange('quota', e.target.value)}
                                          className="form-select"
                                        >
                                          {quotaTypes.map(quota => (
                                            <option key={quota} value={quota}>{quota}</option>
                                          ))}
                                        </select>
                                      </div>
                                      <div className="col-md-4">
                                        <label className="form-label fw-semibold">Seat Type *</label>
                                        <select
                                          value={newCutoff.seat_type}
                                          onChange={(e) => handleCutoffInputChange('seat_type', e.target.value)}
                                          className="form-select"
                                        >
                                          {seatTypes.map(type => (
                                            <option key={type} value={type}>{type}</option>
                                          ))}
                                        </select>
                                      </div>
                                      <div className="col-md-4">
                                        <label className="form-label fw-semibold">Category *</label>
                                        <select
                                          value={newCutoff.category}
                                          onChange={(e) => handleCutoffInputChange('category', e.target.value)}
                                          className="form-select"
                                        >
                                          {categories.map(category => (
                                            <option key={category} value={category}>{category}</option>
                                          ))}
                                        </select>
                                      </div>
                                      <div className="col-md-4">
                                        <label className="form-label fw-semibold">Closing Rank *</label>
                                        <input
                                          type="number"
                                          value={newCutoff.closing_rank}
                                          onChange={(e) => handleCutoffInputChange('closing_rank', parseInt(e.target.value))}
                                          className="form-control"
                                          placeholder="Enter closing rank"
                                          min="1"
                                        />
                                      </div>
                                      <div className="col-md-3">
                                        <label className="form-label fw-semibold">Opening Rank</label>
                                        <input
                                          type="number"
                                          value={newCutoff.opening_rank}
                                          onChange={(e) => handleCutoffInputChange('opening_rank', parseInt(e.target.value))}
                                          className="form-control"
                                          placeholder="Enter opening rank"
                                          min="0"
                                        />
                                      </div>
                                      <div className="col-md-3">
                                        <label className="form-label fw-semibold">Seats Available</label>
                                        <input
                                          type="number"
                                          value={newCutoff.seats_available}
                                          onChange={(e) => handleCutoffInputChange('seats_available', parseInt(e.target.value))}
                                          className="form-control"
                                          placeholder="Available seats"
                                          min="0"
                                        />
                                      </div>
                                      <div className="col-md-3">
                                        <label className="form-label fw-semibold">Seats Filled</label>
                                        <input
                                          type="number"
                                          value={newCutoff.seats_filled}
                                          onChange={(e) => handleCutoffInputChange('seats_filled', parseInt(e.target.value))}
                                          className="form-control"
                                          placeholder="Filled seats"
                                          min="0"
                                        />
                                      </div>
                                    </div>
                                    
                                    <div className="d-flex gap-2 mt-4">
                                      <button
                                        onClick={saveCutoff}
                                        disabled={saving}
                                        className="btn btn-primary d-flex align-items-center gap-2"
                                      >
                                        {saving ? (
                                          <>
                                            <span className="spinner-border spinner-border-sm" role="status"></span>
                                            Saving...
                                          </>
                                        ) : (
                                          <>
                                            <Save size={16} />
                                            {editingCutoffId ? 'Update Cutoff' : 'Save Cutoff'}
                                          </>
                                        )}
                                      </button>
                                      <button
                                        onClick={() => {
                                          setShowAddCutoffForm(null);
                                          setEditingCutoffId(null);
                                          resetCutoffForm();
                                        }}
                                        className="btn btn-secondary"
                                      >
                                        Cancel
                                      </button>
                                    </div>
                                  </div>
                                </motion.div>
                              )}

                              {/* Cutoffs Table */}
                              {branch.cutoffs && branch.cutoffs.length > 0 ? (
                                <div className="table-responsive">
                                  <table className="table table-bordered table-hover">
                                    <thead className="table-light">
                                      <tr>
                                        <th>Year</th>
                                        <th>Round</th>
                                        <th>Quota</th>
                                        <th>Seat Type</th>
                                        <th>Category</th>
                                        <th>Opening</th>
                                        <th>Closing</th>
                                        <th>Actions</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {branch.cutoffs.sort((a, b) => {
                                        if (a.year !== b.year) return b.year.localeCompare(a.year);
                                        return a.round_number - b.round_number;
                                      }).map((cutoff) => (
                                        <tr key={cutoff.id}>
                                          <td>{cutoff.year}</td>
                                          <td>{cutoff.round_name}</td>
                                          <td>{cutoff.quota}</td>
                                          <td>{cutoff.seat_type}</td>
                                          <td>{cutoff.category}</td>
                                          <td>{cutoff.opening_rank || 'N/A'}</td>
                                          <td>{cutoff.closing_rank}</td>
                                          <td>
                                            <div className="d-flex gap-2">
                                              <button
                                                onClick={() => editCutoff(cutoff)}
                                                className="btn btn-sm btn-outline-primary"
                                              >
                                                <Edit3 size={14} />
                                              </button>
                                              <button
                                                onClick={() => deleteCutoff(cutoff.id)}
                                                className="btn btn-sm btn-outline-danger"
                                              >
                                                <Trash2 size={14} />
                                              </button>
                                            </div>
                                          </td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                </div>
                              ) : (
                                <div className="alert alert-info d-flex align-items-center gap-2">
                                  <Target size={16} />
                                  <span>No cutoffs added yet. Add cutoffs to track admission data.</span>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="alert alert-info d-flex align-items-center gap-2">
                      <BookOpen size={16} />
                      <span>No branches added yet. Add branches to manage cutoffs.</span>
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>

        {institutes.length === 0 && !loading && (
          <div className="text-center py-5">
            <div className="card border-0 shadow-lg mx-auto" style={{ maxWidth: '600px' }}>
              <div className="card-body p-5">
                <div className="mb-4">
                  <GraduationCap className="text-primary mx-auto mb-3" size={64} />
                </div>
                <h3 className="h4 text-dark mb-3">No institutes found</h3>
                <p className="text-muted mb-4">
                  {searchQuery || typeFilter !== 'all' || stateFilter !== 'all'
                    ? 'No institutes match your search criteria.'
                    : 'Get started by adding your first JoSAA institute.'
                  }
                </p>
                <button
                  onClick={() => {
                    resetInstituteForm();
                    setShowAddForm(true);
                    setEditingId(null);
                  }}
                  className="btn btn-primary"
                >
                  Add Your First Institute
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default JoSAAAdmin;